"use client";

import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { JobNotFoundContent } from "@artha/ui";
import type { JobNotFoundJob } from "@artha/ui";
import { PageLayout } from "@/components/layout/PageLayout";
import { formatSalary, shouldHideSalaryBadge } from "@/lib/utils";
import type { JobItemDto } from "@artha/api-contracts";
import { formatExperience } from "@/components/jobs/jobDetailsUtils";

type JobNotFoundPageClientProps = {
  profilePath: string;
  username: string;
  creatorName?: string | null;
  creatorAvatarUrl?: string | null;
  jobs: JobItemDto[];
  sectionLabel: "similar" | "creator";
};

export default function JobNotFoundPageClient({
  profilePath,
  username,
  creatorName,
  creatorAvatarUrl,
  jobs,
  sectionLabel,
}: JobNotFoundPageClientProps) {
  const router = useRouter();
  const t = useTranslations("jobs");

  const mappedJobs: JobNotFoundJob[] = jobs.map((job, index) => ({
    id: job.id || job.ref_id || job.slug || job.url || String(index),
    title: job.title ?? "",
    company: job.company ?? "",
    logo: job.logo,
    location: job.location,
    city: job.city,
    state: job.state,
    country: job.country,
    salary: formatSalary(job),
    experience: formatExperience(job),
    workMode: job.job_type ?? "",
    skills: job.skills?.length ? job.skills : job.keywords,
    hideSalary: shouldHideSalaryBadge(job),
    href: job.slug
      ? `/@${username}/jobs/${encodeURIComponent(job.slug)}`
      : undefined,
  }));

  return (
    <PageLayout className="relative bg-[#FFF8E8] !p-0">
      <JobNotFoundContent
        profilePath={profilePath}
        creatorName={creatorName}
        creatorAvatarUrl={creatorAvatarUrl}
        jobs={mappedJobs}
        sectionLabel={sectionLabel === "creator" ? t("creatorJobsLabel") : t("similarJobsLabel")}
        illustrationSrc="/images/job-not-found-illustration.png"
        title={t("jobNotFoundTitle")}
        subtitle={t("jobNotFoundSubtitle")}
        noJobsText={t("jobNotFoundNoJobs")}
        viewProfileText={t("viewProfile")}
        onJobClick={(job) => {
          if (job.href) router.push(job.href);
        }}
      />
    </PageLayout>
  );
}
